import { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '';

/**
 * Hook for managing user consent
 * Handles checking consent status, recording consent, and updating analytics consent
 */
export function useConsent() {
  const [needsConsent, setNeedsConsent] = useState(null); // null = loading, true = needs consent, false = has consent
  const [analyticsConsent, setAnalyticsConsent] = useState(null); // null = loading/unknown, true/false = optional analytics opt-in
  const [consent, setConsent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Check if user has given required consent
   */
  const checkConsent = async () => {
    const token = localStorage.getItem('authToken');
    console.log('[useConsent] Checking consent, token exists:', !!token);

    if (!token) {
      console.log('[useConsent] No token found, skipping consent check');
      setNeedsConsent(false); // Not authenticated - can't check consent
      setAnalyticsConsent(false); // No auth → no analytics consent → no tracking
      setLoading(false);
      return;
    }

    try {
      // Re-checks happen after in-session login/register — hold the gate
      // (loading=true) so the app doesn't flash through before the answer.
      setLoading(true);
      console.log('[useConsent] Fetching consent status from:', `${API_URL}/api/consent/check`);
      const response = await fetch(`${API_URL}/api/consent/check`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to check consent status');
      }

      const data = await response.json();
      console.log('[useConsent] Consent check response:', data);
      setNeedsConsent(data.needsConsent);
      setAnalyticsConsent(data.analyticsConsent === true);
      setLoading(false);
    } catch (err) {
      console.error('[useConsent] Error checking consent:', err);
      setError(err.message);
      // IMPORTANT: Default to requiring consent if check fails (fail-safe)
      setNeedsConsent(true);
      setAnalyticsConsent(false); // Fail closed — no tracking if check failed
      setLoading(false);
    }
  };

  /**
   * Get full consent details
   */
  const getConsent = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      return null;
    }

    try {
      const response = await fetch(`${API_URL}/api/consent`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to get consent');
      }

      const data = await response.json();
      setConsent(data.consent);
      return data.consent;
    } catch (err) {
      console.error('Error getting consent:', err);
      setError(err.message);
      return null;
    }
  };

  /**
   * Record user consent (initial or update)
   */
  const recordConsent = async (consentData) => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('Not authenticated');
    }

    console.log('[useConsent] Recording consent:', consentData);

    try {
      const response = await fetch(`${API_URL}/api/consent`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(consentData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to record consent');
      }

      const data = await response.json();
      console.log('[useConsent] Consent recorded successfully:', data);
      setConsent(data.consent);
      setNeedsConsent(false);
      setAnalyticsConsent(data.consent?.analytics === true);
      return data.consent;
    } catch (err) {
      console.error('[useConsent] Error recording consent:', err);
      setError(err.message);
      throw err;
    }
  };

  /**
   * Update analytics consent only (post-value opt-in)
   */
  const updateAnalyticsConsent = async (analytics) => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('Not authenticated');
    }

    try {
      const response = await fetch(`${API_URL}/api/consent/analytics`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ analytics })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update analytics consent');
      }

      const data = await response.json();
      setConsent(data.consent);
      setAnalyticsConsent(data.consent?.analytics === true);
      return data.consent;
    } catch (err) {
      console.error('Error updating analytics consent:', err);
      setError(err.message);
      throw err;
    }
  };

  /**
   * Get consent audit log
   */
  const getAuditLog = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('Not authenticated');
    }

    try {
      const response = await fetch(`${API_URL}/api/consent/audit`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to get audit log');
      }

      const data = await response.json();
      return data.auditLog;
    } catch (err) {
      console.error('Error getting audit log:', err);
      throw err;
    }
  };

  // Check consent status on mount
  useEffect(() => {
    console.log('[useConsent] useEffect triggered, calling checkConsent');
    checkConsent();
  }, []);

  return {
    needsConsent,
    analyticsConsent,
    consent,
    loading,
    error,
    checkConsent,
    getConsent,
    recordConsent,
    updateAnalyticsConsent,
    getAuditLog
  };
}
